import React, { useState, useEffect, useMemo } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { BUSINESS_TYPES, getBusinessType } from './businessTypes';
import AutocompleteInput from './AutocompleteInput';
import { PlusCircle } from 'lucide-react';

// Category অনুযায়ী প্রোডাক্ট রেজিস্টার করার জন্য -- এখানে শুধু Main Category,
// Sub Category আর নাম দিয়ে একটা খালি (স্টক ০) প্রোডাক্ট তৈরি হয়, দাম/স্টক
// পরে Purchase পেজ থেকে যোগ করতে হবে।
const CategoryInventorySetup = () => {
  const [products, setProducts] = useState([]);
  const [mainCategory, setMainCategory] = useState(BUSINESS_TYPES[0]);
  const [subCategory, setSubCategory] = useState('');
  const [productName, setProductName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    return onSnapshot(collection(db, 'products'), (snap) => {
      setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, []);

  const subCategoryOptions = useMemo(() => (
    [...new Set(
      products.filter((p) => getBusinessType(p) === mainCategory).map((p) => p.category).filter(Boolean)
    )]
  ), [products, mainCategory]);

  // সব প্রোডাক্ট Main Category -> Sub Category অনুযায়ী গুছিয়ে দেখানোর জন্য
  const grouped = useMemo(() => {
    const byMain = {};
    products.forEach((p) => {
      const main = getBusinessType(p);
      const sub = p.category || '(No sub category)';
      byMain[main] = byMain[main] || {};
      byMain[main][sub] = byMain[main][sub] || [];
      byMain[main][sub].push(p.name);
    });
    return byMain;
  }, [products]);

  const addProduct = async () => {
    const name = productName.trim();
    if (!name) return alert('Please enter a product name!');

    const exists = products.some((p) => (p.name || '').toLowerCase().trim() === name.toLowerCase() && getBusinessType(p) === mainCategory);
    if (exists) return alert(`"${name}" already exists under ${mainCategory}!`);

    setSaving(true);
    try {
      await addDoc(collection(db, 'products'), {
        name,
        businessType: mainCategory,
        category: subCategory.trim(),
        price: 0,
        costPrice: 0,
        stock: 0,
        createdAt: serverTimestamp()
      });
      setProductName('');
      setSubCategory('');
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div className="white-box" style={{ marginBottom: '20px' }}>
        <h3 style={{ marginTop: 0, color: '#4a00e0' }}>➕ Register a New Product</h3>
        <p style={{ color: '#888', fontSize: '13px', marginBottom: '20px' }}>
          Registers the product with 0 stock and 0 price under the right category — add stock and price later from Purchase.
        </p>

        <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>Main Category</label>
        <select value={mainCategory} onChange={(e) => { setMainCategory(e.target.value); setSubCategory(''); }} className="input-field">
          {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>

        <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>Sub Category</label>
        <AutocompleteInput
          value={subCategory}
          onChange={setSubCategory}
          options={subCategoryOptions}
          placeholder="e.g. Serum, Panjabi, Hand made jewellery..."
          className="input-field"
        />

        <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>Product Name</label>
        <input
          value={productName}
          onChange={(e) => setProductName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addProduct()}
          placeholder="e.g. Thanaka Face Pack"
          className="input-field"
        />

        <button onClick={addProduct} disabled={saving} className="btn-confirm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: saving ? 0.7 : 1 }}>
          <PlusCircle size={16} /> {saving ? 'Adding...' : 'Add Product'}
        </button>
      </div>

      <div className="white-box">
        <h3 style={{ marginTop: 0, color: '#4a00e0' }}>📋 Current Category Structure</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#0f2a4a', color: '#fff' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>Main Category</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Sub Category</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Product Name</th>
              </tr>
            </thead>
            <tbody>
              {BUSINESS_TYPES.filter((main) => grouped[main]).map((main) => (
                Object.entries(grouped[main]).map(([sub, names], subIdx) => (
                  names.map((name, nameIdx) => (
                    <tr key={`${main}-${sub}-${name}`} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '6px 8px' }}>{subIdx === 0 && nameIdx === 0 ? main : ''}</td>
                      <td style={{ padding: '6px 8px' }}>{nameIdx === 0 ? sub : ''}</td>
                      <td style={{ padding: '6px 8px' }}>{name}</td>
                    </tr>
                  ))
                ))
              ))}
            </tbody>
          </table>
          {products.length === 0 && (
            <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>No products yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryInventorySetup;
